import axios from 'axios';
import _, { isEmpty, uniq } from 'lodash';
import { Rectangle, rectanglesDistinctArea } from '../../utils/rectangle';
import { pricing_model } from './pricing_model.graphql';

interface FormulaModel {
	operator: string;
	values: number[];
}

interface PricingRulePropertyModel {
	propertyID: number;
	functionName: string;
}

interface IContext {
	sides: any;
	items: any;
	design: MPlaza.Design;
	rule: any;
}

enum PricingElements {
	Tutti = 1,
	AllElementsNoTemplateItems = 18,
	AllTexts = 8,
	AllCustomText = 9,
	SingleElement = 10,
	AllImages = 11,
	UploadedImages = 12,
	AllTextArt = 13,
	AllCustomTextArt = 14,
	AllCustomElements = 15,
	ClipartImages = 16,
	TemplatesImages = 17,
	OnlyVectorImages = 19,
	OnlyRasterImages = 20
}

const operatorsFunctions = (operator) => {
	const func = [
		{ operator: '>', func: (a, b) => a > b },
		{ operator: '<', func: (a, b) => a < b },
		{ operator: '=', func: (a, b) => a == b },
		{ operator: '>=', func: (a, b) => a >= b },
		{ operator: '<=', func: (a, b) => a <= b },
		{ operator: '!=', func: (a, b) => a != b }
	].find((of) => of.operator == operator)?.func;

	if (func == null) throw new Error(`Function  for operator ${operator} not found`);

	return func;
};

declare const Logger: { info: (...params: (string | number)[]) => void };

// type PricingRuleConditionsModel = Pick<pricing_model['rules'][0], 'conditions'>;
// type PricingRuleConditionModel = Pick<PricingRuleConditionsModel[0],''>;

export class DesignPriceCalculatorRuleFactory {
	static create = async (
		product: any,
		apiUrl?: string,
		token?: string,
		isOwin?: boolean,
		cloudFlareApiUrl?: string,
		clientLang?: string
	): Promise<DesignPriceCalculatorRuleBased> => {
		let calculator: DesignPriceCalculatorRuleBased = new DesignPriceCalculatorRuleBased(apiUrl, token, isOwin, cloudFlareApiUrl, clientLang);
		await calculator.init(product).catch((e) => console.error(e));
		return calculator;
	};
}

export class DesignPriceCalculatorRuleBased {
	public colorID: number = -1;
	private product?: any;
	private pricings?: pricing_model[];
	private pricing?: pricing_model;
	private properties?: PricingRulePropertyModel[];

	private config: any = {
		isOwin: true,
		apiUrl: 'https://test-api.zakeke.com/api/graphql',
		cloudFlareApiUrl: null,
		token: 'Gw6IL2Q7e66r3osC16_T6XSlsrnuUAHmw59Y3-qJv2LaPPV-nuzDkC0eOGAU8ef_i94HLlu8vDVXpk9MAWBtwrXWN7_XizPAeIfKXArfol19Ek7wmZMR3zRQWwHChqPmYDJMrI3WeCIQGRJxsR7q8hh5xywTqdOwVi6LT7_8-qP9jkc_tdBUPiMUrT6HI1i1ygnEGEk6Eoa97zvbVGRE3NRt-W5nXKGv4_INB_VBdpOihHVXIzl7PtchqPyBr7AaW5Hdrbh_KoFwEhLTcTixXpxfzOcjecbyqFHw20fNFlg2ny6Cb375i7hpnFWD7Q4lj9xI3R6jbkpFHsQEFfBK-UF1f9vli0liCWQ3WdAYGQEmUYx7QjCy240GuR_cK6c28JHoabSGQdW91K7KEAug4o19nokKfzbWoZM7WD4T4RVXNB_ob_gjeYKsK7EJIt6qGiPpnJEbSeFCjz1EhE31Jw6MO-oawNSASxgYk1D6Z3XkLw1G',
		clientLang: null
	};

	constructor(apiUrl?: string, token?: string, isOwin?: boolean, cloudFlareApiUrl?: string, clientLang?: string) {
		if (apiUrl !== undefined) this.config.apiUrl = apiUrl + 'graphql';

		if (token !== undefined) this.config.token = token;

		if (isOwin !== undefined) this.config.isOwin = isOwin;

		if (cloudFlareApiUrl) this.config.cloudFlareApiUrl = cloudFlareApiUrl + 'graphql';

		if (clientLang) this.config.clientLang = clientLang;
	}

	init = (product: any): Promise<void> => {
		this.product = product;
		return new Promise((resolve, reject) => {
			try {
				this.getPricings(product, (pricings, properties) => {
					this.pricings = pricings;
					this.properties = properties;
					resolve();
				});
			} catch (error) {
				reject(error);
			}
		});
	};

	getPricings = async (product, callback: (pricings, properties?: PricingRulePropertyModel[]) => void) => {
		if (this.pricings) {
			callback(this.pricings, this.properties);
		} else {
			const query = {
				name: 'pricingsConfigurationQuery',
				query: `query pricingsQuery($first: Int!, $productID: Long!, $languageCulture: String) {
                                    pricingProperties { propertyID functionName }
                                    pricingsDetailed(first: $first, productID: $productID, isForFrontEnd: true, languageCulture: $languageCulture) {
                                        edges {
                                            node {
                                                id
                                                description
                                                infoPricingText
                                                priceDescriptionEnabled
                                                priceStructure
                                                products {
                                                    productID
                                                }
                                                printTypes {
                                                    printTypeID
                                                }
                                                rules {
                                                    ruleID
                                                    propertyID
                                                    allMatchType
                                                    elementID
                                                    elementTitle
                                                    isSetupPrice
                                                    priceType
													simplified
                                                    targets {
                                                        area
                                                        color
                                                        side
                                                        targetID
                                                    }
                                                    conditions {
                                                        conditionID
                                                        formula
                                                        position
                                                        priceToAdd
                                                        quantity
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }`,
				variables: { first: 50, productID: product.get('modelID'), languageCulture: this.config.clientLang }
			};
			const axiosConfig = {
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json'
				}
			};

			if (this.config.isOwin) {
				axiosConfig['headers']['Authorization'] = 'Bearer ' + this.config.token;
			} else {
				axiosConfig['headers']['X-Auth-Token'] = this.config.token;
			}

			const requestData = {
				type: 'POST',
				url: Zakeke.config.graphqlUrl,
				data: JSON.stringify(query),
				contentType: 'application/json',
				dataType: 'json'
			};

			if (this.config.isOwin) {
				requestData['Authorization'] = 'Bearer ' + this.config.token;
			} else {
				requestData['X-Auth-Token'] = this.config.token;
			}

			try {
				if (typeof this.config.cloudFlareApiUrl !== 'undefined' && this.config.cloudFlareApiUrl !== null && this.config.cloudFlareApiUrl !== '') {
					const responseCF = await axios.post(this.config.cloudFlareApiUrl, query, axiosConfig);
					callback(
						responseCF.data.data.pricingsDetailed.edges.map((edge) => edge.node),
						responseCF.data.data.pricingProperties
					);
					return;
				}
			} catch (error) {
				console.error('Error in CloudFlare API', error);
			}

			try {
				const response = await axios.post(Zakeke.config.graphqlUrl, query, axiosConfig);
				callback(
					response.data.data.pricingsDetailed.edges.map((edge) => edge.node),
					response.data.data.pricingProperties
				);
			} catch (error) {
				console.error('Error in API', error);
			}
		}
	};

	getPricing = (design: any) => {
		if (this.pricings) {
			// let pricings;
			let pricingAndSideIDs;
			let pricing = this.pricings.find((pricing) => pricing.products.find((p) => p.productID == this.product.get('modelID')));
			if (pricing && this.colorID !== -1) {
				let sides = this.product
					.get('colors')
					.find((color) => color.get('colorID') === this.colorID)
					.get('sides')
					.map((side) => {
						return side.get('sideID');
					});
				pricingAndSideIDs = [[pricing, sides]];
			}
			//se non trovo il pricing associato al prodotto cerco quello associato al print type

			if (!pricing && this.colorID !== -1) {
				// if (this.colorID !== -1) {
				let sidesIDs = this.product
					.get('colors')
					.find((color) => color.get('colorID') === this.colorID)
					.get('sides')
					.map((side) => {
						return side.get('sideID');
					});

				let sideIDsAndPrintTypesID = sidesIDs.map((sideID) => {
					const sidePrintType = design.get('sidesPrintTypes').find((sPT) => sPT.get('sideID') === sideID);

					return [sideID, sidePrintType ? sidePrintType.get('printTypeID') : design.get('printTypeID')];
				});
				let pricingSidesMapping = new Map();
				let notRegroupedPricingAndSideIDs = [
					sideIDsAndPrintTypesID.map((sidePT) => {
						return [sidePT[0], this.pricings?.find((pricing) => pricing.printTypes.find((p) => p.printTypeID === sidePT[1])) || null];
					})
				];
				notRegroupedPricingAndSideIDs[0].forEach(([sideID, pricing]) => {
					if (!pricingSidesMapping.get(pricing)) pricingSidesMapping.set(pricing, [sideID]);
					else {
						let value = pricingSidesMapping.get(pricing);
						value.push(sideID);
						pricingSidesMapping.set(pricing, value);
					}
				});

				pricingAndSideIDs = Array.from(pricingSidesMapping);
			}

			// if (pricingAndSideIDs) {
			// 	let firstPrice = pricingAndSideIDs[0];
			// 	if (firstPrice) this.pricing = firstPrice[0];
			// }

			return pricingAndSideIDs;
		}
	};

	distinct = (value, index, self) => self.indexOf(value) === index;

	ElementSizeFunction = (pContext, formula: FormulaModel) => {
		let result = { result: false, multiplier: 0, items: pContext.items };

		//qui dentro il mio context contiene già tutti gli item di tipo text, image o textart (eventualmente saranno già filtrati dagli
		//elements della rule. In ogni caso il calcolo della size in cm sarà lo stesso:
		//quidni basta fare width / side.ppcm e sai i cm (width viene preso dal json del designitem)
		//e height / side.ppcm;
		//poi confronto i valore ottenuti ognuno col valore corrispondente dell'action (che quindi in questo caso ha 2 valori)

		if (!formula.values || formula.values.length == 0) throw 'values missing in formula';
		if (formula.values == null || formula.values.length == 0) throw 'ElementSize invalid value';

		pContext.items.forEach((item) => {
			//prendo il json, lo leggo , mi prendo il testo e conto i caratteri
			const myJson = JSON.parse(item.get('json'))[1];

			var width = myJson.data.transformsData.dimensions.width;
			var height = myJson.data.transformsData.dimensions.height;

			//qui dentro tutti i designItemAreas del'item stanno per forza sullo stesso lato, quindi basta prendere il primo
			var sideID = item.get('areas').at(0).get('sideID');

			var modelSide = pContext.sides.find((s) => s.get('sideID') == sideID);

			if (width != null && height != null && modelSide != null && modelSide.get('ppcm') > 0) {
				var itemWidth = width / modelSide.get('ppcm');
				var itemHeight = height / modelSide.get('ppcm');
				var itemArea = itemHeight * itemWidth;

				if (operatorsFunctions(formula.operator)(itemArea, formula.values[0])) {
					result.result = true;
					result.multiplier += 1;
					result.items = result.items.filter((x) => x != item);
				}
			}
		});
		return result;
	};

	NumberOfElementsSimplifiedFunction = (pContext, formula: FormulaModel) => {
		return {
			result: pContext.items.length > 0,
			multiplier: pContext.items.length,
			items: pContext.items
		};
	};

	LinesLengthFunction = (pContext, formula: FormulaModel) => {
		//fai tutto quello che devi fare in modo da ottenere sempre i due numeri da cofrontare
		//in questo caso la lunghezza dei caratteri inseriti con il valore inserito nella action dal seller

		//per ora sono escluse le textart
		let result: any = { result: false, multiplier: 0 };

		//qui dentro il mio context contiene già item di tipo text (o di custom text)
		//in ogni deisgnitem prendo gli elemnti di testo e conto il i caratteri
		if (!formula.values || formula.values.length == 0) throw 'values missing in formula';

		pContext.items.forEach((item) => {
			//prendo il json, lo leggo , mi prendo il testo e conto i caratteri
			const myJson = JSON.parse(item.get('json'))[1];

			var isText = myJson.data.isText;
			var isTextArea = myJson.data.isTextArea;
			var isTextOnPath = myJson.data.isTextOnPath;
			let isTextArt = myJson.data.isTextArt;

			let testo: string = '';
			if (isText != null && isText) testo = myJson.content.toString();
			else if ((isTextArea != null && isTextArea) || (isTextOnPath != null && isTextOnPath)) testo = myJson.data.text.toString();
			if (isTextArt != null && isTextArt) testo = item.get('textArt').get('text');
			if (!isEmpty(testo)) {
				//zero fine riga significa una riga
				const numLines: number = testo.split('\n').length;

				if (operatorsFunctions(formula.operator)(numLines, formula.values[0])) {
					result.result = true;
					result.multiplier += 1;
				}
			}
		});

		return result;
	};

	AmountOfElementsFunction = (pContext, formula: FormulaModel) => {
		//fai tutto quello che devi fare in modo da ottenere sempre i due numeri da cofrontare
		//in questo caso saranno il numero di elementi e il valore inserito nella action dal seller

		let result: any = { result: false, multiplier: 0 };

		////in questa funzione si contano tutti gli items quindi non ci saranno items da escludere nella eventuale successiva action
		//result.MatchedItems = items;
		if (!formula.values || formula.values.length == 0) throw 'values missing in formula';
		if (operatorsFunctions(formula.operator)(pContext.items.length, formula.values[0])) {
			result.result = true;
			result.multiplier = 1;
		}

		return result;
	};

	AmountOfUsedColorsFunction = (pContext, formula: FormulaModel) => {
		/*, i colori sono univoci ma per elemento: devi calcolare quelli per design:
    
            esempio:
    
            immagine1 = ['rosso', 'verde']
            immagine2 = ['verde', 'giallo']
    
            effettivamente hai usato 3 colori, rosso, verde e giallo.
         */
		let result: any = { result: false, multiplier: 0 };
		if (!formula.values || formula.values.length == 0) throw 'values missing in formula';
		////in questa funzione si contano tutti gli items quindi non ci saranno items da escludere nella eventuale successiva action
		////a prescindere dal risultato

		// let colors: string[] = [];

		// pContext.items.forEach(item => {
		//     if (item.get('itemColors') && !isEmpty(item.get('itemColors')))
		//         colors.push(item.get('itemColors'));
		// });

		const colors: string[] = uniq(
			pContext.items.filter((item) => item.get('itemColors') && !isEmpty(item.get('itemColors'))).flatMap((item) => item.get('itemColors'))
		);

		if (operatorsFunctions(formula.operator)(colors.filter(this.distinct).length, formula.values[0])) {
			result.result = true;
			result.multiplier = 1;
		}

		return result;
	};

	PriceByDesignSizeSimplifiedFunction = (pContext, formula: FormulaModel) => {
		let result: any = { result: false, multiplier: 0 };
		let totalItemsArea: number = 0;

		if (!formula.values || formula.values.length < 1) throw 'PriceByDesignSizeSimplifiedFunction requires at least 1 value: range size';

		const rangeSize = formula.values[0]; // e.g., 10
		if (rangeSize <= 0) throw 'Range size must be greater than 0';

		if (pContext.sides && !isEmpty(pContext.sides)) {
			let areas = pContext.sides.flatMap((s) => s.get('areas').models);

			// Calculate the total area covered by design elements (similar to DesignSizeFunction)
			areas.forEach((area) => {
				// Find items that are on this area
				let items: any[] = pContext.items.filter(
					(i) =>
						i
							.get('areas')
							.filter(
								(a) => a.get('colorID') == area.getColorID() && a.get('sideID') == area.getSideID() && a.get('areaID') == area.get('areaID')
							).length > 0
				);

				let modelSide = pContext.sides.find((s) => s.get('sideID') == area.getSideID());
				if (modelSide != null && modelSide.get('ppcm') > 0) {
					const itemsRectangles: Rectangle[] = items
						.map((item) => {
							return JSON.parse(item.get('json'))[1];
						})
						.filter((json) => {
							const isEmptyFillableShape = Boolean(json.data.isImagePlaceholder);
							return !isEmptyFillableShape;
						})
						.map((json) => {
							const width = json.data.transformsData.dimensions.width;
							const height = json.data.transformsData.dimensions.height;
							const xPosition = json.data.transformsData.position.x;
							const yPosition = json.data.transformsData.position.y;
							return new Rectangle(width, height, xPosition - width / 2, yPosition - height / 2);
						});

					totalItemsArea = totalItemsArea + rectanglesDistinctArea(itemsRectangles, modelSide.get('ppcm'));
				}
			});
		}

		// Calculate which range this total area falls into
		if (totalItemsArea > 0) {
			// Find the range: 0-10 = range 0, 10-20 = range 1, 20-30 = range 2, etc.
			const rangeIndex = Math.floor(totalItemsArea / rangeSize);

			result.result = true;
			result.multiplier = rangeIndex;
		}

		return result;
	};

	PriceByAreaSimplifiedFunction = (pContext, formula: FormulaModel) => {
		let result: any = { result: false, multiplier: 0 };
		let totalCanvasArea: number = 0;

		if (!formula.values || formula.values.length < 1) throw 'PriceByAreaSimplifiedFunction requires at least 1 value: range size';

		const rangeSize = formula.values[0]; // e.g., 10
		if (rangeSize <= 0) throw 'Range size must be greater than 0';

		if (pContext.sides && !isEmpty(pContext.sides)) {
			let areas = pContext.sides.flatMap((s) => s.get('areas').models);

			// Calculate the total canvas area (similar to CanvasSizeFunction)
			areas.forEach((area) => {
				var width = area.get('boundsWidth');
				var height = area.get('boundsHeight');

				// If is a resizable canvas, use the current size
				const designOptions = (pContext.design as any)
					?.get('options')
					?.find((x) => x.get('colorID') == area.getColorID() && (x.get('sideID') === area.getSideID() || x.get('sideID') === null));

				if (designOptions && designOptions.get('width') && designOptions.get('height')) {
					width = designOptions.get('width');
					height = designOptions.get('height');
				}

				var modelSide = pContext.sides.find((s) => s.get('sideID') == area.getSideID());

				if (modelSide != null && modelSide.get('ppcm') > 0) {
					var areaWidth = width / modelSide.get('ppcm');
					var areaHeight = height / modelSide.get('ppcm');

					// Round 2 decs
					areaWidth = Math.round(areaWidth * 100) / 100;
					areaHeight = Math.round(areaHeight * 100) / 100;
					var areaSize = areaWidth * areaHeight;

					totalCanvasArea += areaSize;
				}
			});
		}

		// Calculate which range this total area falls into
		if (totalCanvasArea > 0) {
			// Find the range: 0-10 = range 0, 10-20 = range 1, 20-30 = range 2, etc.
			const rangeIndex = Math.floor(totalCanvasArea / rangeSize);

			result.result = true;
			result.multiplier = rangeIndex;
		}

		return result;
	};

	CanvasSizeFunction = (pContext: IContext, formula: FormulaModel) => {
		if (pContext.rule.simplified) {
			return this.PriceByAreaSimplifiedFunction(pContext, formula);
		}

		let result: any = { result: false, multiplier: 0 };

		if (!formula.values || formula.values.length == 0) throw 'values missing in formula';
		if (pContext.sides && !isEmpty(pContext.sides)) {
			let areas = pContext.sides.flatMap((s) => s.get('areas').models);

			let customizedAreas: any[] = [];

			//prendiamo le aree con almeno un item (ricorda che gli items del context sono già filtrati per target/elements)
			areas.forEach((area) => {
				//per ogni area cerco, fra tutti gli items quelli che stanno su quell'area.
				if (
					pContext.items.filter(
						(i) =>
							i
								.get('areas')
								.filter(
									(a) => a.get('colorID') == area.getColorID() && a.get('sideID') == area.getSideID() && a.get('areaID') == area.get('areaID')
								).length > 0
					).length > 0
				) {
					customizedAreas = [...customizedAreas, area];
				}
			});

			//a questo punto , per ogni area mi calcolo le misure in cm (dividendo i bound dell'area per i ppcm del side a cui appartiene
			//e eseguo la funzione con l'operatore e i due values del seller
			customizedAreas.forEach((area) => {
				var width = area.get('boundsWidth');
				var height = area.get('boundsHeight');

				// If is a resizable canvas, use the current size
				const designOptions = (pContext.design as any)
					?.get('options')
					?.find((x) => x.get('colorID') == area.getColorID() && (x.get('sideID') === area.getSideID() || x.get('sideID') === null));

				if (designOptions && designOptions.get('width') && designOptions.get('height')) {
					width = designOptions.get('width');
					height = designOptions.get('height');
				}

				var modelSide = pContext.sides.find((s) => s.get('sideID') == area.getSideID());

				if (modelSide != null && modelSide.get('ppcm') > 0) {
					var areaWidth = width / modelSide.get('ppcm');
					var areaHeight = height / modelSide.get('ppcm');

					// Round 2 decs
					areaWidth = Math.round(areaWidth * 100) / 100;
					areaHeight = Math.round(areaHeight * 100) / 100;

					if (
						operatorsFunctions(formula.operator)(areaWidth, formula.values[0]) &&
						operatorsFunctions(formula.operator)(areaHeight, formula.values[1])
					) {
						result.result = true;
						result.multiplier += 1;
					}
				}
			});
		}

		return result;
	};

	DesignSizeFunction = (pContext, formula: FormulaModel) => {
		if (pContext.rule.simplified) {
			return this.PriceByDesignSizeSimplifiedFunction(pContext, formula);
		}
		let result: any = { result: false, multiplier: 0 };
		let itemsArea: number = 0;

		if (!formula.values || formula.values.length == 0) throw 'values missing in formula';
		if (pContext.sides && !isEmpty(pContext.sides)) {
			let areas = pContext.sides.flatMap((s) => s.get('areas').models);

			//prendiamo le aree con almeno un item (ricorda che gli items del context sono già filtrati per target/elements)
			areas.forEach((area) => {
				//per ogni area cerco, fra tutti gli items quelli che stanno su quell'area.
				let items: any[] = pContext.items.filter(
					(i) =>
						i
							.get('areas')
							.filter(
								(a) => a.get('colorID') == area.getColorID() && a.get('sideID') == area.getSideID() && a.get('areaID') == area.get('areaID')
							).length > 0
				);
				// seconda versione somma delle aree di ogni item
				let modelSide = pContext.sides.find((s) => s.get('sideID') == area.getSideID());
				if (modelSide != null && modelSide.get('ppcm') > 0) {
					const itemsRectangles: Rectangle[] = items.map((item) => {
						const json = JSON.parse(item.get('json'))[1];
						const width = json.data.transformsData.dimensions.width;
						const height = json.data.transformsData.dimensions.height;
						const xPosition = json.data.transformsData.position.x;
						const yPosition = json.data.transformsData.position.y;
						return new Rectangle(width, height, xPosition - width / 2, yPosition - height / 2);
					});
					itemsArea = itemsArea + rectanglesDistinctArea(itemsRectangles, modelSide.get('ppcm'));
					console.log('itemsArea', itemsArea);
				}
			});
		}
		if (itemsArea > 0 && operatorsFunctions(formula.operator)(itemsArea, formula.values[0])) {
			result.result = true;
			result.multiplier += 1;
		}
		return result;
	};

	DesignTextLengthFunction = (pContext, formula: FormulaModel) => {
		let result: any = { result: false, multiplier: 0 };

		//fai tutto quello che devi fare in modo da ottenere sempre i due numeri da cofrontare
		//in questo caso saranno il totale dei caratteri di tutto il design (quindi non sul singolo item)
		//e il valore inserito nella action dal seller

		if (!formula.values || formula.values.length == 0) throw 'values missing in formula';

		let totalLength: number = 0;
		pContext.items.forEach((item) => {
			//prendo il json, lo leggo , mi prendo il testo e conto i caratteri
			const myJson = JSON.parse(item.get('json'))[1];

			let isText = myJson.data.isText;
			let isTextArea = myJson.data.isTextArea;
			let isTextOnPath = myJson.data.isTextOnPath;
			let isTextArt = myJson.data.isTextArt;

			let testo: string = '';

			if (isText != null && isText) testo = myJson.content.toString();
			else if ((isTextArea != null && isTextArea) || (isTextOnPath != null && isTextOnPath)) testo = myJson.data.text.toString();
			else if (isTextArt != null && isTextArt) testo = item.get('textArt').get('text');

			if (!isEmpty(testo)) {
				totalLength += testo
					.trim()
					.split(' ')
					.join('')
					.replace(/(\r\n|\n|\r)/gm, '').length;
			}
		});

		//questo è un caso  in cui il moltiplicatore può essere solo zero oppure uno e i matcheditems non devono influenzare
		//l'eventuale action successiva
		if (operatorsFunctions(formula.operator)(totalLength, formula.values[0])) {
			result.result = true;
			result.multiplier = 1;
		}

		return result;
	};

	CustomizedSidesFunction = (pContext, formula: FormulaModel) => {
		//fai tutto quello che devi fare in modo da ottenere sempre i due numeri da cofrontare
		//in questo caso saranno il numero di elementi e il valore inserito nella action dal seller

		let result: any = { result: false, multiplier: 0 };
		if (!formula.values || formula.values.length == 0) throw 'values missing in formula';

		//var modelSides = ((ModelSideInfoList)pContext["sides"]).ToList();

		let customizedSides: number = 0;

		//elimina il foreach
		pContext.sides.forEach((side) => {
			//dato un certo side e un certo designItem, per sapere se quel side ha elementi personalizzati

			if (pContext.items.filter((item) => item.get('areas').filter((area) => area.get('sideID') == side.get('sideID')).length > 0).length > 0) {
				customizedSides++;
			}
		});

		// We must check that we have at last one side to check.
		// If pContext.sides.length == 0 then this mean that the current rule is not valid for this
		// variation

		if (pContext.sides.length > 0 && operatorsFunctions(formula.operator)(customizedSides, formula.values[0])) {
			result.result = true;
			result.multiplier = 1;
		}

		return result;
	};

	TextLengthFunction = (pContext, formula: FormulaModel) => {
		//fai tutto quello che devi fare in modo da ottenere sempre i due numeri da cofrontare
		//in questo caso la lunghezza dei caratteri inseriti con il valore inserito nella action dal seller

		//per ora sono escluse le textart
		let result: any = { result: false, multiplier: 0 };

		//qui dentro il mio context contiene già item di tipo text (o di custom text)
		//in ogni deisgnitem prendo gli elemnti di testo e conto il i caratteri

		if (!formula.values || formula.values.length == 0) throw 'values missing in formula';

		pContext.items.forEach((item) => {
			//prendo il json, lo leggo , mi prendo il testo e conto i caratteri
			// Newtonsoft.Json.Linq.JToken myJson =
			//                    Newtonsoft.Json.JsonConvert.DeserializeObject<List<Newtonsoft.Json.Linq.JToken>>(
			//                        item.Json)[1];
			let myJson = JSON.parse(item.get('json'))[1];

			let isText = myJson.data.isText;
			let isTextArea = myJson.data.isTextArea;
			let isTextOnPath = myJson.data.isTextOnPath;
			let isTextArt = myJson.data.isTextArt;

			let testo: string = '';
			if (isText != null && isText) testo = myJson.content.toString();
			else if ((isTextArea != null && isTextArea) || (isTextOnPath != null && isTextOnPath)) testo = myJson.data.text.toString();
			else if (isTextArt != null && isTextArt) testo = item.get('textArt').get('text');

			if (!isEmpty(testo) && operatorsFunctions(formula.operator)(testo.trim().split(' ').join('').length, formula.values[0])) {
				result.result = true;
				result.multiplier += 1;
			}
		});

		return result;
	};

	conditionsToConditionsGroupByFormula = (conditions) => {
		const gb = _.groupBy(conditions, (c) => (c?.formula ? c?.formula : ''));
		return Object.keys(gb).map((k) => {
			return { formula: k, position: gb[k][0]?.position, conditions: gb[k] };
		});
	};

	getFunctionName = (properties, rule) => {
		try {
			return properties?.filter((p) => p.propertyID == rule.propertyID).map((p) => p.functionName)[0];
		} catch (error) {
			throw "function or operator don't exist in dictionaries";
		}
	};

	// clearPricings = () => {
	// 	this.pricing = undefined;
	// 	this.pricings = undefined;
	// };

	// property functions
	// functions = [
	//     {"TextLength":  this.TextLengthFunction},
	//     {"ElementSize":  this.ElementSizeFunction},
	//     {"LinesLength":  this.LinesLengthFunction},
	//     {"AmountOfElements":  this.AmountOfElementsFunction},
	//     {"AmountOfUsedColors":  this.AmountOfUsedColorsFunction},
	//     {"CanvasSize":  this.CanvasSizeFunction},
	//     {"DesignTextLength":  this.DesignTextLengthFunction},
	//     {"CustomizedSides":  this.CustomizedSidesFunction},
	//     {"DesignSizeFunction":  this.DesignSizeFunction}, new
	// ]

	GetContextForRule = (rule, product, design, sidesOnWhichThePriceIsEnabled, attributeMatchColorId: number | null = null) => {
		let context: IContext = { sides: [], items: [], design , rule};

		let allItems: any[] = design.get('designItems').toArray();
		var selectedItems: any[] = []; // Item1, item 2, Item3, Item4

		//verifichiamo prima i targets e poi gli elements
		let colorName: string = ''; //ci serve per i target

		const attributeColor = attributeMatchColorId ? product.get('colors').find((c) => c.get('colorID') == attributeMatchColorId) : null;
		const colore = product.get('colors').find((c) => c.get('colorID') == this.colorID);

		if (colore == null || colore == undefined) return context;

		colorName = attributeColor ? attributeColor.get('name') : colore.get('name');

		const sides = colore.get('sides');
		context.sides = [];

		if (rule.targets.length == 0) {
			// Se non ho target vuol dire che non sto filtrando per nulla
			//e quindi devo prendere TUTTI gli item di TUTTO il prodotto personalizzato (a prescindere da area, lato o  variante)
			selectedItems = allItems;
			context.sides = sides.toArray();
		} else {
			rule.targets.forEach(
				(target) => {
					let skip: boolean = false;

					let side: any = null;

					let currentItems = allItems;
					// Se il target filtra per variante e la mia non corrisponde, salto completamente questo target e non aggiungo nulla ai selectedtItems
					//altrimenti per ora mi tengo tutti gli items del design (che ora stanno in sideItems)
					if (!isEmpty(target.color) && target.color.toLowerCase() != colorName.toLowerCase()) {
						skip = true; //continue;
					} else {
						// Add sides of this color
						for (const side of sides.toArray()) {
							if (context.sides.indexOf(side) === -1) context.sides.push(side);
						}
					}

					// se c'è un filtro per side name selezioni gli item che stanno solo su quel side
					if (!isEmpty(target.side)) {
						side = sides.find((s) => s.get('name').toLowerCase() == target.side.toLowerCase());
						if (side == null || !side.get('areas')) skip = true; //continue;
						else {
							let areaItems: any = [];

							// All items that touches the area filter
							const filter = (item: any, area: any) =>
								item.get('areas').filter((a) => {
									return (
										a.get('modelID') == product.get('modelID') &&
										a.get('colorID') == area.getSide().getColor().get('colorID') &&
										a.get('sideID') == area.getSide().get('sideID') &&
										a.get('areaID') == area.get('areaID')
									);
								}).length > 0;

							side.get('areas').forEach((area) => {
								areaItems = areaItems.concat(allItems.filter((item) => filter(item, area)));
							});

							currentItems = areaItems;
						}
					}

					// se c'è un filtro per area name selezioni gli item che stanno su quell'area
					if (!isEmpty(target.area)) {
						let areaItems: any = [];

						//se ci sono due lati, e un area su ogni lato con lo stesso nome. IN questo caso li voglio tutti e due gli item
						let areas: any = [];

						sides.forEach((side) => {
							areas = areas.concat(side.get('areas').filter((a) => a.get('name').toLowerCase() == target.area.toLowerCase()));
						});

						areas.forEach((area: any) => {
							//currentItems potrebbe essere già filtrato per lato, altrimenti contiene tutti gli item (allItems)
							areaItems = [
								...currentItems.filter(
									(i) =>
										i
											.get('areas')
											.filter(
												(a) =>
													a.get('modelID') == product.get('modelID') &&
													a.get('colorID') == area.getSide().getColor().get('colorID') &&
													a.get('sideID') == area.getSide().get('sideID') &&
													a.get('areaID') == area.get('areaID')
											).length > 0
								)
							];
						});

						//raggruppiamo alla fine
						currentItems = areaItems;
					}

					//eliminiamo eventuali duplicati (raggruppa su ItemGuid perchè su cosmos (a livello di carrello siamo ancora su cosmosDb)
					//l'itemId non esiste ancora
					// currentItems = currentItems.GroupBy(c => c.ItemGuid, (key, c) => c.FirstOrDefault()).ToList();
					if (!skip) {
						selectedItems = [...selectedItems, ...currentItems];
					}
				} //fine foreach target
			);
		}

		//chiedi come mettere gli id corretti nell'enumerativo degli elemnts
		if (rule.elementID == PricingElements.AllTexts) {
			selectedItems = selectedItems.filter((p) => {
				let j = JSON.parse(p.get('json'))[1];
				return (
					(j.data.isText != null && j.data.isText) ||
					(j.data.isTextArea != null && j.data.isTextArea) ||
					(j.data.isTextOnPath != null && j.data.isTextOnPath) ||
					(j.data.isTextArt != null && j.data.isTextArt)
				);
			});
		} else if (rule.elementID == PricingElements.AllElementsNoTemplateItems) {
			selectedItems = selectedItems.filter((p) => {
				let j = JSON.parse(p.get('json'))[1];
				return !j.data.isTemplateElement;
			});
		} else if (rule.elementID == PricingElements.AllCustomText) {
			//per gestire i vecchi casi in cui isTemplateElement non c'era: i null li considero custom
			selectedItems = selectedItems.filter((p) => {
				var j = JSON.parse(p.get('json'))[1];
				return (
					(j.data.isTemplateElement != null &&
						!j.data.isTemplateElement &&
						((j.data.isText != null && j.data.isText) ||
							(j.data.isTextArea != null && j.data.isTextArea) ||
							(j.data.isTextOnPath != null && j.data.isTextOnPath) ||
							(j.data.isTextArt != null && j.data.isTextArt))) ||
					(j.data.isTemplateElement == null &&
						((j.data.isText != null && j.data.isText) ||
							(j.data.isTextArea != null && j.data.isTextArea) ||
							(j.data.isTextOnPath != null && j.data.isTextOnPath) ||
							(j.data.isTextArt != null && j.data.isTextArt)))
				);
			});
		} else if (rule.elementID == PricingElements.AllImages) {
			selectedItems = selectedItems
				.filter((i) => i.get('imageID') > 0)
				.filter((i) => {
					const json = JSON.parse(i.get('json'))[1];
					return !json.data.isTemplateElement;
				});
		} else if (rule.elementID == PricingElements.UploadedImages) {
			selectedItems = selectedItems
				.filter((p) => {
					if (p.get('imageID') > 0) {
						if (p.get('imageVisitorCode') || p.get('imageCustomerCode')) return true;

						if (p.get('imageVisitorID') > 0 || p.get('imageCustomerID') > 0) return true;
					}

					return false;
				})
				.filter((i) => {
					const json = JSON.parse(i.get('json'))[1];
					return !json.data.isTemplateElement;
				});
		} else if (rule.elementID == PricingElements.ClipartImages) {
			selectedItems = selectedItems
				.filter((p) => {
					if (p.get('imageID') > 0) {
						if (p.get('imageVisitorCode') || p.get('imageCustomerCode')) return false;

						if (p.get('imageVisitorID') > 0 || p.get('imageCustomerID') > 0) return false;

						return true;
					}
					return false;
				})
				.filter((i) => {
					const json = JSON.parse(i.get('json'))[1];
					return !json.data.isTemplateElement;
				});
		} else if (rule.elementID == PricingElements.TemplatesImages) {
			selectedItems = selectedItems
				.filter((p) => p.get('imageID') > 0)
				.filter((i) => {
					const json = JSON.parse(i.get('json'))[1];
					return json.data.isTemplateElement;
				});
		} else if (rule.elementID == PricingElements.SingleElement) {
			selectedItems = selectedItems.filter((i) => i.get('name').toLowerCase() == rule.elementTitle.toLowerCase());
		} else if (rule.elementID == PricingElements.AllTextArt) {
			selectedItems = selectedItems.filter((p) => {
				let json = JSON.parse(p.get('json'))[1];
				return json.data.isTextArt != null && json.data.isTextArt;
			});
		} else if (rule.elementID == PricingElements.AllCustomTextArt) {
			//selectedtItems = selectedtItems.Where(x => x.isTexArt == true && item.isCustom);
		} else if (rule.elementID == PricingElements.AllCustomElements) {
			selectedItems = selectedItems.filter((p) => {
				const json = JSON.parse(p.get('json'))[1];
				return (
					(p.get('imageID') > 0 &&
						!json.data.isTemplateElement &&
						(p.get('imageVisitorCode') || p.get('imageCustomerCode') || p.get('imageVisitorID') > 0 || p.get('imageCustomerID') > 0)) ||
					(json.data.isTemplateElement != null &&
						!json.data.isTemplateElement &&
						((json.data.isText != null && json.data.isText) ||
							(json.data.isTextArea != null && json.data.isTextArea) ||
							(json.data.isTextOnPath != null && json.data.isTextOnPath) ||
							(json.data.isTextArt != null && json.data.isTextArt))) ||
					(json.data.isTemplateElement == null &&
						((json.data.isText != null && json.data.isText) ||
							(json.data.isTextArea != null && json.data.isTextArea) ||
							(json.data.isTextOnPath != null && json.data.isTextOnPath) ||
							(json.data.isTextArt != null && json.data.isTextArt)))
				);
			});
		} else if (rule.elementID == PricingElements.UploadedImages) {
			selectedItems = selectedItems.filter((p) => {
				if (p.get('imageID') > 0) {
					if (p.get('imageVisitorCode') || p.get('imageCustomerCode')) return true;

					if (p.get('imageVisitorID') > 0 || p.get('imageCustomerID') > 0) return true;
				}

				return false;
			});
		} else if (rule.elementID === PricingElements.OnlyVectorImages) {
			selectedItems = selectedItems.filter((item) => item.get('imageID') > 0 && item.get('imageType') === 'Vector');
		} else if (rule.elementID === PricingElements.OnlyRasterImages) {
			selectedItems = selectedItems.filter(
				(item) => (item.get('imageID') > 0 && item.get('imageType') === 'Raster') || item.get('imageType') === 'Vector+Raster'
			);
		}

		// Filter all items that are not printable
		selectedItems = selectedItems.filter((designItem) => {
			return !designItem.get('constraints') || designItem.get('constraints').get('isPrintable');
		});

		// eseguo una distinct
		const selectedItemsGroupByItemGuild = _.groupBy(selectedItems, (c) => c.get('itemGuid'));
		context.items = Object.keys(selectedItemsGroupByItemGuild).map((k) => selectedItemsGroupByItemGuild[k][0]);

		context.items = context.items.filter((x) => {
			if (x.get('areas').length === 0) return false;

			const sideID = x.get('areas').at(0).get('sideID');
			return sidesOnWhichThePriceIsEnabled.includes(sideID);
		});
		return context;
	};

	// controllo se ci sono pricing sul prodotto, altrimenti sui print types
	isUsingAmountOfColors = (printTypeID: number): boolean => {
		if (this.pricings) {
			let pricing = this.pricings.find((pricing) => pricing.products.find((p) => p.productID == this.product.get('modelID')));
			if (pricing) return pricing.rules.find((r) => r.propertyID == 10) != undefined;
			else {
				let pricing = this.pricings.find((pricing) => pricing.printTypes.find((p) => p.printTypeID === printTypeID));
				if (pricing) return pricing.rules.find((r) => r.propertyID == 10) != undefined;
			}
		}
		return false;
	};

	getPremiumPhotosPrice = (design: any, quantity: number) => {
		// Aggiungo il prezzo delle immagini esterne utilizzate
		const imageItemsGroups: any = [];
		let price = 0;

		for (var i = 0; i < design.get('designItems').length; i++) {
			const item: any = design.get('designItems').at(i);
			if (
				item.get('imageID') > 0 &&
				item.get('imagePrice') > 0 &&
				item.get('providerImageID') != null &&
				item.get('providerImageID')?.length > 0 &&
				!imageItemsGroups.find((x) => x.get('providerImageID') === item.get('providerImageID'))
			) {
				imageItemsGroups.push(item);
				let pricePerImage = item.get('imagePrice');

				if (!this.product.get('hasAdditionalFeeOnImagesProvider'))
					pricePerImage =
						item.get('applyImagePriceForQuantity') === null || item.get('applyImagePriceForQuantity')
							? item.get('imagePrice') * quantity
							: item.get('imagePrice');
				else pricePerImage = item.get('imagePrice') * quantity;

				price += pricePerImage;
			}
		}

		Logger.info('Premium photo price: ', price);
		return price;
	};
	/**
	 * Get Image Price
	 *
	 * Metodo che calcola il prezzo per le immagini nel design a cui a backoffice
	 * è stato valorizzato un prezzo
	 *
	 * @author f.checchia
	 *
	 * @param design Il design in input
	 * @param {number} quantity La quantità del prodotto che si sta acquistando
	 *
	 * @return {number} Il prezzo complessivo delle immagini per la quantità
	 *
	 * */
	getImagePrice = (design: any, quantity: number) => {
		// Aggiungo il prezzo delle immagini utilizzate
		let price = 0;

		for (var i = 0; i < design.get('designItems').length; i++) {
			const item = design.get('designItems').at(i);
			if (item.get('imageID') > 0 && item.get('imagePrice') > 0 && !item.get('providerImageID')) {
				let pricePerImage = item.get('imagePrice');
				pricePerImage = item.get('imagePrice') * quantity;
				price += pricePerImage;
			}
		}

		Logger.info('Image price: ', price);
		return price;
	};

	getMatchedConditions = (design: any, quantity: number, includePercentage: boolean, attributeMatchColorId: number | null = null) => {
		// this.pricing = this.getPricing(design);
		let pricingsAndSidesIDs = this.getPricing(design);

		// if (!this.pricing) return [];
		if (!pricingsAndSidesIDs) return [];

		let matchedConditions: any = [];

		// this.pricing.rules.forEach((rule) => {
		pricingsAndSidesIDs.forEach((pricingAndSidesIDs) => {
			if (!pricingAndSidesIDs[0]) return;
			// this.pricing = pricingAndSidesIDs[0];

			let sideIDs = pricingAndSidesIDs[1];
			pricingAndSidesIDs[0].rules.forEach((rule) => {
				if (!includePercentage && rule.priceType == 1) return;

				const context = this.GetContextForRule(rule, this.product, design, sideIDs, attributeMatchColorId);

				// If we don't have sides to check, we are in the wrong
				// variant probably, just skip it.
				if (context.sides.length === 0) return;

				const conditionsGroupByformula = this.conditionsToConditionsGroupByFormula(rule.conditions);
				let forEachBreakException = {};
				try {
					conditionsGroupByformula
						.sort((c1, c2) => (c1 && c2 && c1.position > c2.position ? 1 : -1))
						.forEach((conditionGroupByFormula) => {
							//per ogni gruppo (le cui conditions avranno tutte la stessa formula) estraggo dalla formula l'operatore e il values e richiamo la function
							let formulaObject: FormulaModel = JSON.parse(conditionGroupByFormula.formula);

							if (!formulaObject.operator) throw 'operator missing in formula';

							// if (!formulaObject.values || formulaObject.values.length == 0) throw ("values missing in formula");

							// conditionGroupByFormula.conditions.forEach( condition => {
							const functionName = this.getFunctionName(this.properties, rule) + 'Function';

							//richiama la funzione col nome preso dalla property e con l'operatore preso dalla conditions (che in questo gruppo hanno la stessa formula)
							const result: any = this[functionName](context, formulaObject);

							// //sia nel caso any che all mi calcolo il prezzo da aggiungere.
							// //Nel caso any se il risultato è true non vado alla successiva condition
							if (result.result) {
								//in ogni gruppo prendo il priceToAdd della condition con la quantità più vicina alla quantità acquistata.
								let condition = conditionGroupByFormula.conditions
									.filter((g) => g.quantity <= quantity)
									.sort((c1, c2) => c2.quantity - c1.quantity)[0];

								//controlla condition: se è null vai avanti
								if (condition != null) {
									const priceToAdd = condition.priceToAdd;
									const isSetupPrice = rule.isSetupPrice;
									const multiplier = result.multiplier;
									const priceType = rule.priceType;

									matchedConditions.push({
										priceToAdd,
										isSetupPrice,
										multiplier,
										priceType
									});

									// If some itemes were not matched with this condition, we must continue to check
									// for other conditions
									const functionMatchedAllItems = !result.items || result.items.length == 0;

									if (!rule.allMatchType && result.items) context.items = result.items;

									if (!rule.allMatchType && functionMatchedAllItems) throw forEachBreakException;
								}
							}
						});
				} catch (e) {
					if (e !== forEachBreakException) throw e;
				}
			});
		});

		return matchedConditions;
	};

	calculate = (design: any, quantity: number, attributeMatchColorId: number | null = null, isPricingAdvancedEnabled: boolean = true) => {
		let matchedConditions: any = [];
		let totalPriceToAdd = this.getPremiumPhotosPrice(design, quantity);
		totalPriceToAdd += this.getImagePrice(design, quantity);

		if (isPricingAdvancedEnabled) {
			matchedConditions = this.getMatchedConditions(design, quantity, false, attributeMatchColorId);

			matchedConditions.forEach((matchedCondition) => {
				let conditionPrice = matchedCondition.priceToAdd * matchedCondition.multiplier;
				if (!matchedCondition.isSetupPrice) conditionPrice *= quantity;

				totalPriceToAdd += conditionPrice;
			});
		}

		Logger.info('Total custom price: ', totalPriceToAdd);
		return totalPriceToAdd;
	};

	getPricingAdditionalInfo = (printTypeID: number) => {
		let pricing;
		if (this.pricings) {
			pricing = this.pricings.find((pricing) => pricing.products.find((p) => p.productID == this.product.get('modelID')));
			if (!pricing) pricing = this.pricings.find((pricing) => pricing.printTypes.find((p) => p.printTypeID === printTypeID));
		}
		if (!pricing) return;
		return {
			description: pricing.description,
			priceDescriptionEnabled: pricing.priceDescriptionEnabled,
			infoPricingText: pricing.infoPricingText,
			priceStructure: pricing.priceStructure
		};
	};
}
